import { AlertCircle, FileSignature, UserPlus, FileEdit, Upload, Loader2, LucideLoader2 } from 'lucide-react';
import { ContractDetailDialog } from './ContractDetailDialog';
import { toast } from 'sonner';
import { useState } from 'react';
import { AnyAsyncThunk } from '@reduxjs/toolkit/dist/matchers';
import { useNavigate, useNavigation } from 'react-router-dom';
import { CallAPIPostPromise } from 'Common/commaon';
import { getUserType } from 'slices/userProfile/thunk';
import { Spinner } from 'react-bootstrap';
import {
  Phone,
  Mail,
  Image,
  MapPin,
  ShieldCheck,
  BadgeCheck
} from "lucide-react";

export const actionIconMap: Record<string, React.ReactNode> = {
  // PROFILE
  PROFILE_PIC_MISSING: <Image className="w-6 h-6" />,
  PROFILE_ADDRESS_INCOMPLETE: <MapPin className="w-6 h-6" />,

  // COMPANY
  COMPANY_PHONE_MISSING: <Phone className="w-6 h-6" />,
  COMPANY_EMAIL_MISSING: <Mail className="w-6 h-6" />,
  INSURANCE_DOC_MISSING: <ShieldCheck className="w-6 h-6" />,
  LICENSE_DOC_MISSING: <BadgeCheck className="w-6 h-6" />,

  // CONTRACT
  CONTRACT_HOMEOWNER_SIGN_PENDING: <FileSignature className="w-6 h-6" />,
  CONTRACT_CONTRACTOR_SIGN_PENDING: <FileSignature className="w-6 h-6" />,
  CONTRACT_CONTRACTOR_TO_SUB_CONTRACTOR_SIGN_PENDING:<FileSignature className="w-6 h-6" />,
  CONTRACT_CONTRACTOR_TO_CONTRACTOR_SIGN_PENDING:<FileSignature className="w-6 h-6" />,
  // FALLBACK
  DEFAULT: <AlertCircle className="w-6 h-6" />,
};

interface ActionItemProps {
  type: 'urgent' | 'important';
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel: string;
  loading:any;
  id:any;
  onAction: () => void;
}

function ActionItem({ type,loading,id, icon, title, description, actionLabel, onAction }: ActionItemProps) {
  const borderColor = type === 'urgent' ? 'border-l-red-500' : 'border-l-amber-500';
  const iconColor = type === 'urgent' ? 'text-red-600' : 'text-amber-600';

  return (
    <div className={`action-required-item action-required-item--${type} border-l-4 ${borderColor} border-solid rounded-xl p-6 flex items-center justify-between gap-8`}>
      <div className="flex items-center gap-4 flex-1">
        <div className={`action-required-item__icon ${iconColor} flex-shrink-0`}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-[500] text-gray-900 mb-1.5 text-[15px]">{title}</h4>
          <p className="text-sm text-gray-700 leading-relaxed">{description}</p>
        </div>
      </div>
      <button
        onClick={()=>{
          onAction()
        }}
        className="action-required-item__button outline-none items-center flex border-0 text-white px-7 py-3.5 rounded-xl text-sm font-[500] transition-all duration-200 whitespace-nowrap"
      >
        {loading ===id &&     <Spinner animation="border" className='w-[20px] h-[20px] mr-2' role="status">
          </Spinner>}
        {actionLabel}
      </button>
    </div>
  );
}

export interface ActionItem {
  type: 'urgent' | 'important';
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel: string;
}

interface ActionRequiredProps {
  actions?: ActionItem[];
  afterAction?:any
}
export interface Contract {
  id: string;
  projectName: string;
  projectDescription: string;
  projectAddress: string;
  contractorName: string;
  contractorCompany: string;
  contractorEmail: string;
  contractorPhone: string;
  contractAmount: number;
  startDate: string;
  estimatedCompletionDate: string;
  contractDate: string;
  contractorSignedDate: string;
  scope: string[];
}
export function ActionRequired({ actions,afterAction }: ActionRequiredProps) {
  const defaultActions: ActionItem[] = [
    {
      type: 'urgent',
      icon: <FileSignature className="w-6 h-6" />,
      title: 'Contract awaiting homeowner signature',
      description: '123 Main Street - Kitchen Remodel',
      actionLabel: 'Review Now',
    },
    {
      type: 'urgent',
      icon: <UserPlus className="w-6 h-6" />,
      title: 'Subcontractor agreement not signed',
      description: 'Electrical contractor for Oak Avenue project',
      actionLabel: 'Sign Now',
    },
    {
      type: 'important',
      icon: <FileEdit className="w-6 h-6" />,
      title: 'Draft contract incomplete',
      description: '456 Elm Street - Bathroom Renovation',
      actionLabel: 'Complete',
    },
    {
      type: 'important',
      icon: <Upload className="w-6 h-6" />,
      title: 'W9 form missing from profile',
      description: 'Required for tax documentation',
      actionLabel: 'Upload',
    }
  ];
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [selectedActionType, setSelectedActionType] = useState(null);
  const [loading,setLoading]=useState(false)
  const [showAll, setShowAll] = useState(false)
  const displayActions = actions ||[]
  const visibleActions = showAll ? displayActions : displayActions.slice(0, 3)
  const hiddenActionCount = Math.max(0, displayActions.length - visibleActions.length)
  const navigation = useNavigate()
 
 
  return (
    <div className="dashboard-panel action-required-panel bg-white border border-gray-200 rounded-xl p-7 shadow-sm">
      <div className="action-required-panel__header flex items-center gap-3 mb-7">
        <div className="p-2.5 bg-red-100 rounded-xl">
          <AlertCircle className="w-6 h-6 text-red-600" />
        </div>
        <div>
          <div className="text-xl font-[500] text-gray-900 tracking-tight">Action Required</div>
          <div className="text-sm text-gray-600 mt-0.5">Items that need your immediate attention</div>
        </div>
        <span className="action-required-panel__count">{displayActions.length}</span>
      </div>
      <div className="space-y-4">
        {visibleActions.map((action:any, index) => (
          <ActionItem id={action.entityId} loading={loading} onAction={()=>{
           if(action.code ==="CONTRACT_CONTRACTOR_SIGN_PENDING" ||action.code ==="CONTRACT_CONTRACTOR_TO_CONTRACTOR_SIGN_PENDING"){
            setSelectedContract(action.entityId)
            setSelectedActionType(action.code)
           }
           if(action.code==="CONTRACT_CONTRACTOR_TO_SUB_CONTRACTOR_SIGN_PENDING"){
            navigation(`/create-contract?tab=Subcontractor&id=${action.entityId}&action=sign`)
           }
           if(action.entityType==="profile"){
            navigation("/pages-profile-settings")
           }
           if(action.entityType==="company"){
            navigation("/pages-profile-settings#companyDetails")

           }
           if(action.code==="CONTRACT_HOMEOWNER_SIGN_PENDING"){
            setLoading(action.entityId)
            CallAPIPostPromise("/contract/resend-contract", {
              projectId: action.entityId,
              userType: getUserType(),
            })
              .then(() => {
              })
              .finally(() => {
                toast.success("Email Sent to Homeowner")
                setLoading(false)
              });
           }
          }} key={index} {...action} icon={actionIconMap[action.code]} />
        ))}
      </div>
      {displayActions.length > 3 && (
        <button
          type="button"
          className="action-required-panel__toggle"
          onClick={() => setShowAll((current) => !current)}
          aria-expanded={showAll}
        >
          {showAll ? "Show less" : `View all ${displayActions.length} actions`}
          <i className={`bi ${showAll ? "bi-chevron-up" : "bi-chevron-down"}`} aria-hidden="true" />
          {!showAll && <span>{hiddenActionCount} more</span>}
        </button>
      )}
      {selectedContract && (
        <ContractDetailDialog
          contractID={selectedContract}
          selectedActionType={selectedActionType}
          onClose={() => setSelectedContract(null)}
          onSign={()=>{
            setSelectedContract(null);
            toast.success("Contract Signed")
            afterAction()
          }}
        />
      )}
    </div>
  );
}
